import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button, Table, Select } from "antd";
import Layout from "../../Layout";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";

// --- INTERFACES ---
interface CareMilestoneItem {
    key: string;
    date: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    milestone: string;
    status: string;
    statusType: "overdue" | "upcoming" | "doctor_confirm";
    preferredChannel: "Gọi" | "Zalo" | "WhatsApp";
    noteR22?: boolean;
    onlyZalo?: boolean;
}

interface ServiceAppointmentItem {
    key: string;
    time: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    service: string;
    status: string;
    statusType: "pending" | "today" | "unconfirmed" | "booked";
}

interface UnscheduledCardItem {
    key: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    cardName: string;
    remaining: string;
    expiryDate: string;
    isUrgentExpiry?: boolean;
}

export default function LichChamSocPage() {
    // State quản lý tab: "milestones" (Mốc chăm sóc) | "service_schedule" (Lịch dùng dịch vụ)
    const [activeTab, setActiveTab] = useState<string>("service_schedule");

    // State quản lý Drawer chi tiết khách hàng bên phải
    const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

    // State trạng thái nhân viên CSKH góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // -------------------------------------------------------------
    // DATA TAB 1: MỐC CHĂM SÓC
    // -------------------------------------------------------------
    const milestoneData: CareMilestoneItem[] = [
        {
            key: "1",
            date: "19/01/2026",
            customerCode: "KH-19950",
            customerName: "Chị L.B.T",
            phone: "+61 4*** *** 118",
            category: "Clinic",
            milestone: "T+30 gợi ý buổi tiếp",
            status: "Quá hạn (khách đã về Úc)",
            statusType: "overdue",
            preferredChannel: "WhatsApp",
            noteR22: true
        },
        {
            key: "2",
            date: "27/08",
            customerCode: "KH-12087",
            customerName: "Chị Đ.T.L",
            phone: "091***4436",
            category: "Clinic",
            milestone: "T+30 buổi 4/5",
            status: "Quá hạn · dời 2 lần",
            statusType: "overdue",
            preferredChannel: "Zalo"
        },
        {
            key: "3",
            date: "04/09",
            customerCode: "KH-13377",
            customerName: "Chị M.T.K",
            phone: "094***1187",
            category: "Clinic",
            milestone: "T+30",
            status: "Quá hạn · khách chưa trả lời",
            statusType: "overdue",
            preferredChannel: "Zalo",
            onlyZalo: true
        },
        {
            key: "4",
            date: "15/10",
            customerCode: "KH-17311",
            customerName: "Chị H.T.M",
            phone: "090***0928",
            category: "Clinic",
            milestone: "T+24h hỏi thăm",
            status: "Sắp tới · hôm nay",
            statusType: "upcoming",
            preferredChannel: "Zalo"
        },
        {
            key: "5",
            date: "15/10",
            customerCode: "KH-14421",
            customerName: "Anh L.V.T",
            phone: "098***2204",
            category: "Newhair",
            milestone: "T+24h hỏi thăm",
            status: "Sắp tới · hôm nay",
            statusType: "upcoming",
            preferredChannel: "Zalo"
        }
    ];

    // -------------------------------------------------------------
    // DATA TAB 2: LỊCH DÙNG DỊCH VỤ (CHUẨN ACCORDING TO IMAGE)
    // -------------------------------------------------------------
    const appointmentsData: ServiceAppointmentItem[] = [
        {
            key: "1",
            time: "18/12 10:00",
            customerCode: "KH-19950",
            customerName: "Chị L.B.T",
            phone: "+61 4*** *** 118",
            category: "Clinic",
            service: "Tư vấn · Khám + K·Lift buổi 1 (giữ chỗ)",
            status: "Chờ cọc",
            statusType: "pending"
        },
        {
            key: "2",
            time: "15/10 14:00",
            customerCode: "KH-16002",
            customerName: "Chị N.H.A",
            phone: "093***5521",
            category: "Clinic",
            service: "Tái khám · Bác sĩ khám phản ứng (BS-0207)",
            status: "Hôm nay",
            statusType: "today"
        },
        {
            key: "3",
            time: "17/10 09:00",
            customerCode: "KH-18050",
            customerName: "Chị B.T.H",
            phone: "093***4470",
            category: "PTTM",
            service: "Tái khám · Tái khám 7 ngày, cắt chỉ",
            status: "Chưa xác nhận",
            statusType: "unconfirmed"
        },
        {
            key: "4",
            time: "20/10 10:00",
            customerCode: "KH-12087",
            customerName: "Chị Đ.T.L",
            phone: "091***4436",
            category: "Clinic",
            service: "Thực hiện · Phác đồ K·Renewal buổi 4/5",
            status: "Chưa xác nhận",
            statusType: "unconfirmed"
        },
        {
            key: "5",
            time: "15/11 08:30",
            customerCode: "KH-15890",
            customerName: "Anh P.Q.D",
            phone: "090***8813",
            category: "Newhair",
            service: "Thực hiện · Aftercare buổi 2/6",
            status: "Đã đặt",
            statusType: "booked"
        }
    ];

    // DATA THẺ CÒN BUỔI NHƯNG CHƯA CÓ LỊCH
    const unscheduledCardsData: UnscheduledCardItem[] = [
        {
            key: "1",
            customerCode: "KH-16002",
            customerName: "Chị N.H.A",
            phone: "093***5521",
            category: "Clinic",
            cardName: "Phác đồ K·Lift · Basic · 3 buổi",
            remaining: "2 buổi",
            expiryDate: "13/04/2027"
        },
        {
            key: "2",
            customerCode: "KH-17311",
            customerName: "Chị H.T.M",
            phone: "090***0928",
            category: "Clinic",
            cardName: "Phác đồ K·Lift · Advanced · 3 buổi",
            remaining: "2 buổi",
            expiryDate: "14/04/2027"
        },
        {
            key: "3",
            customerCode: "KH-14421",
            customerName: "Anh L.V.T",
            phone: "098***2204",
            category: "Newhair",
            cardName: "Aftercare Newhair · 24 tháng · 12 buổi",
            remaining: "12 buổi",
            expiryDate: "14/10/2028"
        },
        {
            key: "4",
            customerCode: "KH-12087",
            customerName: "Chị Đ.T.L",
            phone: "091***4436",
            category: "Clinic",
            cardName: "Phác đồ K·Renewal · Basic · 5 buổi",
            remaining: "2 buổi",
            expiryDate: "30/11/2026",
            isUrgentExpiry: true
        }
    ];

    // -------------------------------------------------------------
    // COLUMNS CONFIGURATION
    // -------------------------------------------------------------
    
    // Cột Tab 1: Mốc Chăm Sóc
    const milestoneColumns = [
        {
            title: "Ngày",
            dataIndex: "date",
            key: "date",
            width: 110,
            render: (text: string) => <span className="font-medium text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: 170,
            render: (_: any, record: CareMilestoneItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Mảng",
            dataIndex: "category",
            key: "category",
            width: 80,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>
        },
        {
            title: "Mốc",
            dataIndex: "milestone",
            key: "milestone",
            width: 180,
            render: (text: string) => <span className="font-medium text-gray-900 dark:text-gray-100">{text}</span>
        },
        {
            title: "Trạng thái",
            key: "status",
            width: 220,
            render: (_: any, record: CareMilestoneItem) => (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={
                        record.statusType === "overdue"
                            ? "text-red-600 font-bold dark:text-red-400"
                            : "text-gray-800 dark:text-gray-200 font-medium"
                    }>
                        {record.status}
                    </span>
                    {record.statusType === "doctor_confirm" && (
                        <span className="bg-[#d97706] text-white text-[10px] px-1.5 py-0.2 rounded font-bold cursor-pointer">
                            [?] bác sĩ chốt
                        </span>
                    )}
                </div>
            )
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 200,
            render: (_: any, record: CareMilestoneItem) => (
                <div className="flex items-center gap-1.5">
                    {record.preferredChannel === "WhatsApp" && (
                        <Button size="small" type="primary" className="!bg-[#3E91AB] text-xs h-6 px-2 font-bold">
                            WhatsApp
                        </Button>
                    )}
                    {record.preferredChannel === "Zalo" && (
                        <Button size="small" type="primary" className="!bg-[#1d4c7d] text-xs h-6 px-2.5 font-bold">
                            Zalo
                        </Button>
                    )}
                    <Button size="small" className="text-xs h-6 px-2.5 border-gray-300">
                        Gọi
                    </Button>
                </div>
            )
        }
    ];

    // Cột Tab 2: Lịch Dùng Dịch Vụ - Bảng 1 (Appointments)
    const appointmentColumns = [
        {
            title: "Thời gian",
            dataIndex: "time",
            key: "time",
            width: 120,
            render: (text: string) => <span className="font-medium text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: 170,
            render: (_: any, record: ServiceAppointmentItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Mảng",
            dataIndex: "category",
            key: "category",
            width: 90,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>
        },
        {
            title: "Dịch vụ",
            dataIndex: "service",
            key: "service",
            width: 320,
            render: (text: string) => <span className="font-medium text-gray-900 dark:text-gray-100">{text}</span>
        },
        {
            title: "Trạng thái",
            key: "status",
            width: 150,
            render: (_: any, record: ServiceAppointmentItem) => {
                let colorClass = "text-gray-800 dark:text-gray-200 font-medium";
                if (record.statusType === "pending" || record.statusType === "unconfirmed") {
                    colorClass = "text-red-700 font-bold dark:text-red-400";
                } else if (record.statusType === "today") {
                    colorClass = "text-gray-900 dark:text-gray-100 font-semibold";
                }
                return <span className={colorClass}>{record.status}</span>;
            }
        },
        {
            title: "",
            key: "action",
            width: 100,
            render: () => (
                <Button size="small" className="text-xs h-6 px-3 border-gray-300 font-medium text-gray-700">
                    Gọi nhắc
                </Button>
            )
        }
    ];

    // Cột Tab 2: Bảng 2 (Unscheduled Cards)
    const unscheduledCardColumns = [
        {
            title: "Khách",
            key: "customer",
            width: 220,
            render: (_: any, record: UnscheduledCardItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Thẻ",
            dataIndex: "cardName",
            key: "cardName",
            width: 380,
            render: (text: string) => <span className="font-medium text-gray-900 dark:text-gray-100">{text}</span>
        },
        {
            title: "Còn lại",
            dataIndex: "remaining",
            key: "remaining",
            width: 110,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium">{text}</span>
        },
        {
            title: "Hạn",
            key: "expiryDate",
            width: 130,
            render: (_: any, record: UnscheduledCardItem) => (
                <span className={record.isUrgentExpiry ? "text-red-600 font-bold dark:text-red-400" : "text-gray-800 dark:text-gray-200 font-medium"}>
                    {record.expiryDate}
                </span>
            )
        },
        {
            title: "",
            key: "action",
            width: 90,
            render: () => (
                <Button size="small" type="primary" className="!bg-[#1d4c7d] text-xs h-6 px-3 font-bold">
                    Đặt lịch
                </Button>
            )
        }
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available": return "#52c41a";
            case "break": return "#d48806";
            case "offline": return "#8c8c8c";
            default: return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Lịch — mốc chăm sóc và lịch dùng dịch vụ | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-xs flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 overflow-hidden gap-4">
                    
                    {/* KHUNG NỘI DUNG CHÍNH BÊN TRÁI */}
                    <div className="space-y-3 overflow-y-auto pb-20 w-full">
                        
                        {/* 1. Header Tiêu đề Lịch */}
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2 gap-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 border-l-4 border-[#1d4c7d] pl-2">
                                <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-sm">
                                    Lịch — mốc chăm sóc và lịch dùng dịch vụ
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                    Vai trò: CSKH
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 hidden xl:inline">
                                    Quy tắc: Gộp Lịch và Đặt lịch (chốt 15/10/2026) · R10 · R36 · mã CS-05
                                </span>
                            </div>
                        </div>

                        {/* 2. Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                        <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1 font-bold tracking-wider rounded text-xs">
                            DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                        </div>

                        {/* 3. Thanh Tab Chuyển Đổi */}
                        <div className="border-b border-gray-200 dark:border-gray-700 flex gap-2 text-xs font-semibold pt-2">
                            <button
                                onClick={() => setActiveTab("milestones")}
                                className={`px-3 py-1.5 font-bold transition-all rounded-t ${
                                    activeTab === "milestones"
                                        ? "bg-[#3E91AB] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                }`}
                            >
                                Mốc chăm sóc
                            </button>
                            <button
                                onClick={() => setActiveTab("service_schedule")}
                                className={`px-3 py-1.5 font-bold transition-all rounded-t ${
                                    activeTab === "service_schedule"
                                        ? "bg-[#3E91AB] text-white shadow-sm"
                                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                                }`}
                            >
                                Lịch dùng dịch vụ
                            </button>
                        </div>

                        {/* hiển thị nội dung theo từng tab */}
                        {activeTab === "milestones" ? (
                            <>
                                <div className="text-gray-500 text-[11px] pt-0.5">
                                    T+24h · T+3 · T+7 · T+30 · T+90 và tái khám — tính từ ngày làm dịch vụ (SOP CSKH mục 2).
                                </div>
                                <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden mt-2">
                                    <Table
                                        columns={milestoneColumns}
                                        dataSource={milestoneData}
                                        pagination={false}
                                        size="small"
                                        className="custom-care-schedule-table"
                                        scroll={{ x: "max-content" }}
                                        rowClassName={(record, index) =>
                                            index % 2 === 1
                                                ? "bg-slate-50/80 dark:bg-[#252525]"
                                                : "bg-white dark:bg-[#1f1f1f]"
                                        }
                                    />
                                </div>
                            </>
                        ) : (
                            /* TAB LỊCH DÙNG DỊCH VỤ - CHUẨN XÁC THEO HÌNH ẢNH MỚI */
                            <div className="space-y-4 pt-1">
                                {/* Dòng mô tả + Nút Đặt lịch góc phải */}
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-gray-600 dark:text-gray-300 text-[11px]">
                                        Buổi tiếp của thẻ liệu trình, lịch bán thêm. Cùng bảng lịch hẹn với app Telesales (R36). Không còn menu "Đặt lịch" riêng.
                                    </div>
                                    <Button type="primary" className="!bg-[#1d4c7d] text-xs font-bold h-7 px-3">
                                        Đặt lịch
                                    </Button>
                                </div>

                                {/* BẢNG 1: LỊCH HẸN DÙNG DỊCH VỤ */}
                                <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                                    <Table
                                        columns={appointmentColumns}
                                        dataSource={appointmentsData}
                                        pagination={false}
                                        size="small"
                                        className="custom-care-schedule-table"
                                        scroll={{ x: "max-content" }}
                                        rowClassName={(record, index) =>
                                            index % 2 === 1
                                                ? "bg-slate-50/80 dark:bg-[#252525]"
                                                : "bg-white dark:bg-[#1f1f1f]"
                                        }
                                    />
                                </div>

                                {/* BẢNG 2: THẺ CÒN BUỔI NHƯNG CHƯA CÓ LỊCH */}
                                <div className="space-y-2 pt-2">
                                    <div className="font-bold text-gray-900 dark:text-gray-100 text-xs">
                                        Thẻ còn buổi nhưng chưa có lịch
                                    </div>
                                    <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                                        <Table
                                            columns={unscheduledCardColumns}
                                            dataSource={unscheduledCardsData}
                                            pagination={false}
                                            size="small"
                                            className="custom-care-schedule-table"
                                            scroll={{ x: "max-content" }}
                                            rowClassName={(record, index) =>
                                                index % 2 === 1
                                                    ? "bg-slate-50/80 dark:bg-[#252525]"
                                                    : "bg-white dark:bg-[#1f1f1f]"
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>

                    {/* 5. DRAWER CHI TIẾT KHÁCH HÀNG BÊN PHẢI */}
                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>
                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}